import * as Sentry from "@sentry/react";
import { lazy, Suspense, type ComponentType } from "react";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider, MutationCache } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";
import { AuthProvider } from "@/hooks/useAuth";
import { AuthGuard } from "@/components/auth/AuthGuard";
import { ModuleGuard } from "@/components/auth/ModuleGuard";
import { MainLayout } from "@/components/layout/MainLayout";
import { useNativeInit } from "@/hooks/useNativeInit";
import { useErrorBreadcrumbs } from "@/hooks/useErrorBreadcrumbs";
import { Loader2 } from "lucide-react";
import ErrorBoundary from "@/components/ErrorBoundary";
import { installGlobalErrorHandlers } from "@/lib/globalErrorHandlers";
import { errorLogger } from "@/lib/errorLogger";

// Install global error handlers before React mounts
installGlobalErrorHandlers();

// Auto-retry dynamic imports once on chunk load failure (stale deployment).
// Uses sessionStorage to prevent infinite reload loops.
function lazyWithRetry(importFn: () => Promise<{ default: ComponentType<any> }>) {
  return lazy(() =>
    importFn().catch((error: unknown) => {
      const key = "chunk_retry_" + importFn.toString().slice(0, 80);
      const hasRetried = sessionStorage.getItem(key);
      if (!hasRetried) {
        sessionStorage.setItem(key, "1");
        window.location.reload();
        // Return a never-resolving promise so React doesn't render stale state
        return new Promise(() => {});
      }
      sessionStorage.removeItem(key);
      throw error;
    })
  );
}

// Eager: critical-path pages
import Login from "./pages/Login";
import AuthCallback from "./pages/AuthCallback";
import NotFound from "./pages/NotFound";

// Lazy: all other pages (with auto-retry on chunk failure)
const Dashboard = lazyWithRetry(() => import("./pages/Dashboard"));
const Documents = lazyWithRetry(() => import("./pages/Documents"));
const QualityLayout = lazyWithRetry(() => import("./pages/quality/QualityLayout"));
const QualityMain = lazyWithRetry(() => import("./pages/quality/QualityMain"));
const QualityInProcessQC = lazyWithRetry(() => import("./pages/quality/QualityInProcessQC"));
const QualityLotTrace = lazyWithRetry(() => import("./pages/quality/QualityLotTrace"));
const Admin = lazyWithRetry(() => import("./pages/Admin"));
const HRLayout = lazyWithRetry(() => import("./pages/hr/HRLayout"));
const TimesheetsWeekView = lazyWithRetry(() => import("./pages/timesheets/WeekView"));
const TimesheetsRules = lazyWithRetry(() => import("./pages/timesheets/Rules"));
const HrStaffDirectory = lazyWithRetry(() => import("./pages/hr/StaffDirectory"));
const HrEmployeeProfileDetail = lazyWithRetry(() => import("./pages/hr/EmployeeProfileDetail"));
const HrAssistant = lazyWithRetry(() => import("./pages/hr/HrAssistant"));
const HrKnowledgeManager = lazyWithRetry(() => import("./pages/hr/KnowledgeManager"));
const HrAuditLog = lazyWithRetry(() => import("./pages/hr/AuditLog"));
const EhOauthCallback = lazyWithRetry(() => import("./pages/integrations/EhOauthCallback"));
const XeroOauthCallback = lazyWithRetry(() => import("./pages/integrations/XeroOauthCallback"));
const AIChat = lazyWithRetry(() => import("./pages/AIChat"));
const Equipment = lazyWithRetry(() => import("./pages/Equipment"));
const CRM = lazyWithRetry(() => import("./pages/CRM"));
const CustomerOnboarding = lazyWithRetry(() => import("./pages/CustomerOnboarding"));
const SupplierPortal = lazyWithRetry(() => import("./pages/SupplierPortal"));
const Contractors = lazyWithRetry(() => import("./pages/Contractors"));
const ContractorRegistration = lazyWithRetry(() => import("./pages/ContractorRegistration"));
const Training = lazyWithRetry(() => import("./pages/Training"));
const Suppliers = lazyWithRetry(() => import("./pages/Suppliers"));
const Consumables = lazyWithRetry(() => import("./pages/Consumables"));
const NPD = lazyWithRetry(() => import("./pages/NPD"));
const PlanningLayout = lazyWithRetry(() => import("./pages/planning/PlanningLayout"));
const PlanningOverview = lazyWithRetry(() => import("./pages/planning/PlanningOverview"));
const PlanningSchedule = lazyWithRetry(() => import("./pages/planning/PlanningSchedule"));
const PlanningSupply = lazyWithRetry(() => import("./pages/planning/PlanningSupply"));
const PlanningMRP = lazyWithRetry(() => import("./pages/planning/PlanningMRP"));
const PlanningSequencing = lazyWithRetry(() => import("./pages/planning/PlanningSequencing"));
const PlanningPerformance = lazyWithRetry(() => import("./pages/planning/PlanningPerformance"));
const PlanningFinance = lazyWithRetry(() => import("./pages/planning/PlanningFinance"));
const PlanningAdmin = lazyWithRetry(() => import("./pages/planning/PlanningAdmin"));
const PlanningDataFlow = lazyWithRetry(() => import("./pages/planning/PlanningDataFlow"));
const PlanningForecasting = lazyWithRetry(() => import("./pages/planning/PlanningForecasting"));
const PlanningOrderBook = lazyWithRetry(() => import("./pages/planning/PlanningOrderBook"));
const WarehouseLayout = lazyWithRetry(() => import("./pages/warehouse/WarehouseLayout"));
const WarehouseInventory = lazyWithRetry(() => import("./pages/warehouse/WarehouseInventory"));
const WarehouseInbound = lazyWithRetry(() => import("./pages/warehouse/WarehouseInbound"));
const WarehouseOutbound = lazyWithRetry(() => import("./pages/warehouse/WarehouseOutbound"));
const WarehouseJRB = lazyWithRetry(() => import("./pages/warehouse/WarehouseJRB"));
const WarehouseAnalyticsOps = lazyWithRetry(() => import("./pages/warehouse/WarehouseAnalyticsOps"));
const SensoryFeedback = lazyWithRetry(() => import("./pages/SensoryFeedback"));
const Messages = lazyWithRetry(() => import("./pages/Messages"));
const ManufacturingLayout = lazyWithRetry(() => import("./pages/manufacturing/ManufacturingLayout"));
const ManufacturingMain = lazyWithRetry(() => import("./pages/manufacturing/ManufacturingMain"));
const ManufacturingLabels = lazyWithRetry(() => import("./pages/manufacturing/ManufacturingLabels"));
const MarketingLayout = lazyWithRetry(() => import("./pages/marketing/MarketingLayout"));
const MarketingMain = lazyWithRetry(() => import("./pages/marketing/MarketingMain"));
const LabelReviewFlow = lazyWithRetry(() => import("./pages/marketing/LabelReviewFlow"));
const Purchasing = lazyWithRetry(() => import("./pages/Purchasing"));
const Settings = lazyWithRetry(() => import("./pages/Settings"));
const StockItems = lazyWithRetry(() => import("./pages/StockItems"));
const Products = lazyWithRetry(() => import("./pages/Products"));
const ProductFormatReview = lazyWithRetry(() => import("./pages/ProductFormatReview"));
const Tasks = lazyWithRetry(() => import("./pages/Tasks"));
const Contacts = lazyWithRetry(() => import("./pages/Contacts"));
const Payments = lazyWithRetry(() => import("./pages/Payments"));
const ProofingDashboard = lazyWithRetry(() => import("./pages/proofing/ProofingDashboard"));
const ProofingProjectDetail = lazyWithRetry(() => import("./pages/proofing/ProofingProjectDetail"));
const RegulatorySourceLibrary = lazyWithRetry(() => import("./pages/proofing/RegulatorySourceLibrary"));
const ComplianceDashboard = lazyWithRetry(() => import("./pages/proofing/ComplianceDashboard"));
const RfqListPage = lazyWithRetry(() => import("./pages/npd/RfqListPage"));
const DispatchQueuePage = lazyWithRetry(() => import("./pages/npd/DispatchQueuePage"));
const CanvasPage = lazyWithRetry(() => import("./pages/canvas/CanvasPage"));
const CertificateViewer = lazyWithRetry(() => import("./pages/certificates/CertificateViewer"));
const PublicQuote = lazyWithRetry(() => import("./pages/PublicQuote"));
const CoAViewer = lazyWithRetry(() => import("./pages/certificates/CoAViewer"));
const TaskAckPage = lazyWithRetry(() => import("./pages/tasks/TaskAckPage"));
// Dev-only: Quote PDF design-port verification harness (route gated below on import.meta.env.DEV).
const QuotePdfPreview = lazyWithRetry(() => import("./pages/dev/QuotePdfPreview"));
const OrderBookPdfPreview = lazyWithRetry(() => import("./pages/dev/OrderBookPdfPreview"));
// Operator print surface for the redesigned Quote PDF (auth-protected, full-page).
const QuotePrint = lazyWithRetry(() => import("./pages/print/QuotePrint"));
// PUBLIC token-gated print surface — what Browserless renders to a PDF (Phase 4).
const QuotePrintPublic = lazyWithRetry(() => import("./pages/print/QuotePrintPublic"));
// Operator print surface for the redesigned Customer Order Book PDF (auth-protected, full-page).
const OrderBookPrint = lazyWithRetry(() => import("./pages/print/OrderBookPrint"));
// Public in-house e-sign page — NDA signing for onboarding (Phase 2h).
const SignDocument = lazyWithRetry(() => import("./pages/SignDocument"));
const SignSupplierAgreement = lazyWithRetry(() => import("./pages/SignSupplierAgreement"));
// Public APP Privacy Policy page (Privacy Act 1988 / APP 1) — linked from the signing notice + footer.
const PrivacyPolicy = lazyWithRetry(() => import("./pages/PrivacyPolicy"));
// Public token-gated completed-document access page — executed NDA + certificate (Phase 2h-B).
const CompletedDocument = lazyWithRetry(() => import("./pages/CompletedDocument"));
// Authenticated Party A countersign queue — feature-gated by VITE_NDA_SEND_ENABLED (Phase 2h-B2).
const NdaCounterSign = lazyWithRetry(() => import("./pages/nda/CounterSign"));

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[400px]" role="status">
      <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" aria-hidden="true" />
      <span className="sr-only">Loading…</span>
    </div>
  );
}

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 2 * 60 * 1000,      // 2 min fresh
      gcTime: 10 * 60 * 1000,         // 10 min GC
      refetchOnWindowFocus: false,
      retry: 1,
    },
  },
  mutationCache: new MutationCache({
    onError: (error: Error, _variables, _context, mutation) => {
      // Log all mutation failures globally — individual onError handlers still run
      errorLogger.log({
        error_type: "supabase_query",
        severity: "error",
        message: error.message,
        stack_trace: error.stack || null,
        action_context: `mutation:${mutation.options.mutationKey?.join("/") || "unknown"}`,
      });
    },
  }),
});

// Breadcrumb tracker must be inside BrowserRouter to use useLocation
function ErrorBreadcrumbTracker() {
  useErrorBreadcrumbs();
  return null;
}

// App content with native initialization
const AppContent = () => {
  // Initialize native features (Capacitor, Capgo, etc.)
  useNativeInit();

  return (
    <BrowserRouter>
      <ErrorBreadcrumbTracker />
      <Suspense fallback={<PageLoader />}>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<Login />} />
        <Route path="/auth/callback" element={<AuthCallback />} />
        <Route path="/onboarding" element={<CustomerOnboarding />} />
        {/* Public supplier self-service portal — token via ?token= query param, no auth */}
        <Route path="/supplier-portal" element={<SupplierPortal />} />
        <Route path="/sensory-feedback" element={<SensoryFeedback />} />
        <Route path="/contractor-register" element={<ContractorRegistration />} />

        {/* External canvas — public, no auth required */}
        <Route path="/canvas/:token" element={<CanvasPage />} />

        {/* Customer certificate viewer — public, token-authenticated */}
        <Route path="/certificates/:token" element={<CertificateViewer />} />

        {/* Customer quote approval — public, token-authenticated */}
        <Route path="/q/:token" element={<PublicQuote />} />

        {/* Public token-gated Quote PDF print surface — rendered to PDF by Browserless */}
        <Route path="/print/quote/token/:token" element={<QuotePrintPublic />} />

        {/* Certificate of Analysis viewer — public, token-authenticated */}
        <Route path="/certificates/coa/:token" element={<CoAViewer />} />

        {/* Task email "Acknowledge" confirmation — public, token-authenticated. Opening it changes nothing; the button POSTs. */}
        <Route path="/tasks/ack/:token" element={<TaskAckPage />} />

        {/* In-house e-sign: NDA signing page — public, token-authenticated */}
        <Route path="/sign/:token" element={<SignDocument />} />

        {/* In-house e-sign: supplier quality-agreement signing — public, token-authenticated (P5a) */}
        <Route path="/sign-agreement/:token" element={<SignSupplierAgreement />} />
        <Route path="/sign-agreement" element={<SignSupplierAgreement />} />

        {/* Public APP Privacy Policy */}
        <Route path="/privacy" element={<PrivacyPolicy />} />

        {/* In-house e-sign: completed/executed NDA access page — public, token-authenticated */}
        <Route path="/completed/:token" element={<CompletedDocument />} />

        {/* Dev-only Quote PDF preview harness — never registered in production builds */}
        {import.meta.env.DEV && (
          <Route path="/dev/quote-preview" element={<QuotePdfPreview />} />
        )}

        {/* Dev-only Order Book PDF preview harness — never registered in production builds */}
        {import.meta.env.DEV && (
          <Route path="/dev/order-book-preview" element={<OrderBookPdfPreview />} />
        )}

        {/* Operator redesigned-Quote-PDF print surface — auth-protected, no app shell */}
        <Route
          path="/print/quote/:id"
          element={
            <AuthGuard>
              <QuotePrint />
            </AuthGuard>
          }
        />

        {/* Operator Customer Order Book PDF print surface — auth-protected, no app shell */}
        <Route
          path="/print/order-book/:customerId"
          element={
            <AuthGuard>
              <OrderBookPrint />
            </AuthGuard>
          }
        />

        {/* Protected app (staff portal) */}
        <Route
          path="/"
          element={
            <AuthGuard>
              <MainLayout />
            </AuthGuard>
          }
        >
          <Route index element={<Dashboard />} />
          <Route path="tasks" element={<Tasks />} />
          {/* Label proofing is gated on the label_proofing module (plan P-7a). RLS
              already hides every proofing row from users without it, so this
              replaces an empty or broken page with a clear "no access" one. */}
          <Route path="proofing" element={<ModuleGuard module="label_proofing"><ProofingDashboard /></ModuleGuard>} />
          <Route path="proofing/regulatory-sources" element={<ModuleGuard module="label_proofing"><RegulatorySourceLibrary /></ModuleGuard>} />
          <Route path="proofing/compliance" element={<ModuleGuard module="label_proofing"><ComplianceDashboard /></ModuleGuard>} />
          <Route path="proofing/:id" element={<ModuleGuard module="label_proofing"><ProofingProjectDetail /></ModuleGuard>} />
          <Route path="crm/*" element={<CRM />} />
          {/* NDA countersign queue (Party A surface, Phase 2h-B2). ALWAYS routed:
              the server-side NDA_SEND_ENABLED flag controls whether countersign
              notification emails are sent, and those emails link here. Gating this
              route on the separate build-time VITE_NDA_SEND_ENABLED desynced from the
              server flag and 404'd the email link. The route is auth-protected and the
              page shows an empty queue when nothing awaits countersignature. */}
          <Route path="nda/countersign" element={<NdaCounterSign />} />
          <Route path="npd/*" element={<NPD />} />
          <Route path="npd/rfq" element={<RfqListPage />} />
          <Route path="npd/dispatch" element={<DispatchQueuePage />} />
          <Route path="stock-items/*" element={<StockItems />} />
          <Route path="products" element={<Products />} />
          <Route path="products/:productId" element={<Products />} />
          <Route path="products/review-formats" element={<ProductFormatReview />} />
          <Route path="quality" element={<QualityLayout />}>
            <Route index element={<QualityMain />} />
            <Route path="in-process-qc" element={<QualityInProcessQC />} />
            <Route path="lot-trace" element={<QualityLotTrace />} />
          </Route>
          <Route path="documents/*" element={<Documents />} />
          <Route path="ai-chat" element={<AIChat />} />
          <Route path="equipment/*" element={<Equipment />} />
          <Route path="suppliers/*" element={<Suppliers />} />
          <Route path="consumables/*" element={<Consumables />} />
          <Route path="contacts/*" element={<Contacts />} />
          <Route path="contractors/*" element={<Contractors />} />
          <Route path="forms/*" element={<ComingSoon module="Forms" />} />
          <Route path="training/*" element={<Training />} />
          <Route path="planning" element={<PlanningLayout />}>
            <Route index element={<Navigate to="overview" replace />} />
            <Route path="overview" element={<PlanningOverview />} />
            <Route path="schedule" element={<PlanningSchedule />} />
            <Route path="supply" element={<PlanningSupply />} />
            <Route path="mrp" element={<PlanningMRP />} />
            <Route path="sequencing" element={<PlanningSequencing />} />
            <Route path="performance" element={<PlanningPerformance />} />
            <Route path="finance" element={<PlanningFinance />} />
            <Route path="forecasting" element={<PlanningForecasting />} />
            <Route path="order-book" element={<PlanningOrderBook />} />
            <Route path="admin" element={<PlanningAdmin />} />
            <Route path="data-flow" element={<PlanningDataFlow />} />
          </Route>
          <Route path="manufacturing" element={<ManufacturingLayout />}>
            <Route index element={<ManufacturingMain />} />
            <Route path="labels" element={<ManufacturingLabels />} />
          </Route>
          <Route path="marketing" element={<MarketingLayout />}>
            <Route index element={<MarketingMain />} />
            <Route path="label-review-flow" element={<LabelReviewFlow />} />
          </Route>
          <Route path="purchasing/*" element={<Purchasing />} />
          <Route path="payments/*" element={<Payments />} />
          <Route path="warehouse" element={<WarehouseLayout />}>
            <Route index element={<Navigate to="inventory" replace />} />
            <Route path="inventory" element={<WarehouseInventory />} />
            <Route path="inbound" element={<WarehouseInbound />} />
            <Route path="outbound" element={<WarehouseOutbound />} />
            <Route path="jrb" element={<WarehouseJRB />} />
            <Route path="analytics" element={<WarehouseAnalyticsOps />} />
          </Route>
          {/* Shipping merged into Warehouse — redirect old routes */}
          <Route path="shipping/stacking-calculator" element={<Navigate to="/warehouse/outbound?tab=pack-dispatch" replace />} />
          <Route path="shipping/*" element={<Navigate to="/warehouse/outbound?tab=ship-dashboard" replace />} />
          <Route path="messages" element={<Messages />} />
          <Route path="settings/*" element={<Settings />} />
          {/* HR — mixed-permission subtree, deliberately split into two gates.
              Timesheets pages are gated on the `timesheets` module (HRLayout);
              strict-HR pages are gated on the `hr` module (each page wraps its
              own <ModuleGuard module="hr">). They are SIBLINGS, not nested under
              HRLayout, so `hr` access never implicitly requires `timesheets`. */}
          <Route path="hr">
            {/* Redirect bare /hr → timesheets (no gate needed for the redirect). */}
            <Route index element={<Navigate to="timesheets" replace />} />
            {/* Timesheets — gated on the `timesheets` module via HRLayout. */}
            <Route element={<HRLayout />}>
              <Route path="timesheets" element={<TimesheetsWeekView />} />
              <Route path="timesheets/rules" element={<TimesheetsRules />} />
            </Route>
            {/* Strict HR — gated on the `hr` module inside each page, independent
                of timesheets so HR permission is fully self-contained. */}
            <Route path="staff" element={<HrStaffDirectory />} />
            <Route path="staff/:profileId" element={<HrEmployeeProfileDetail />} />
            <Route path="assistant" element={<HrAssistant />} />
            <Route path="knowledge" element={<HrKnowledgeManager />} />
            <Route path="audit" element={<HrAuditLog />} />
          </Route>
          {/* Backwards-compat redirects from the pre-HR-restructure paths.
              Any external bookmark to /timesheets still resolves. */}
          <Route path="timesheets" element={<Navigate to="/hr/timesheets" replace />} />
          <Route path="timesheets/rules" element={<Navigate to="/hr/timesheets/rules" replace />} />
          <Route
            path="admin/integrations/employment-hero/callback"
            element={<EhOauthCallback />}
          />
          <Route
            path="admin/integrations/xero/callback"
            element={<XeroOauthCallback />}
          />
          <Route path="admin/*" element={<Admin />} />
          <Route path="*" element={<NotFound />} />
        </Route>

        {/* Public 404 */}
        <Route path="*" element={<NotFound />} />
      </Routes>
      </Suspense>
    </BrowserRouter>
  );
};

function SentryFallback({ eventId }: { eventId: string | null }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-background p-6">
      <div className="w-full max-w-md rounded-lg border bg-card p-8 text-center shadow-sm">
        <h2 className="mb-2 text-xl font-semibold text-foreground">
          Something went wrong
        </h2>
        <p className="mb-6 text-sm text-muted-foreground">
          An unexpected error occurred. Please reload the page to continue.
        </p>
        <div className="flex flex-col gap-3">
          {eventId && (
            <button
              onClick={() => Sentry.showReportDialog({ eventId })}
              className="inline-flex items-center justify-center rounded-md border border-input bg-background px-4 py-2 text-sm font-medium text-foreground hover:bg-accent"
            >
              Report what happened
            </button>
          )}
          <button
            onClick={() => window.location.reload()}
            className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90"
          >
            Reload page
          </button>
        </div>
      </div>
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <TooltipProvider>
      <AuthProvider>
        <Sonner />
        <Sentry.ErrorBoundary fallback={({ eventId }) => <SentryFallback eventId={eventId} />}>
          <ErrorBoundary>
            <AppContent />
          </ErrorBoundary>
        </Sentry.ErrorBoundary>
      </AuthProvider>
    </TooltipProvider>
  </QueryClientProvider>
);

// Temporary placeholder for modules not yet implemented
function ComingSoon({ module }: { module: string }) {
  return (
    <div className="flex flex-col items-center justify-center min-h-[400px] text-center">
      <div className="text-6xl mb-4">🚧</div>
      <h2 className="text-2xl font-semibold mb-2">{module}</h2>
      <p className="text-muted-foreground">
        This module is coming soon in the next phase of development.
      </p>
    </div>
  );
}

export default App;
